"use client";

import { useCallback, useState, type MouseEvent } from "react";
import { ownsContextMenu } from "./use-row-open";
import type { Row, RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import type { RecordRowMenuTarget } from "./record-row-menu";
import type { RecordObjectDef, TableAction } from "../types";

const ROW_MENU_KEYBOARD_INSET = 16;

export interface RowMenuAnchor {
  x: number;
  y: number;
  opener: HTMLElement | null;
}

export interface RowMenuController<TData extends RowData> {
  target: RecordRowMenuTarget | null;
  row: TData | undefined;
  href: string | undefined;
  onRowMenu: ((row: TData, anchor: RowMenuAnchor) => boolean) | undefined;
  close: () => void;
}

export function visibleRowActions<TData extends RowData>(
  actions: TableAction<TData>[] | undefined,
  row: TData,
): TableAction<TData>[] {
  return (actions ?? []).filter((action) => !action.show || action.show(row));
}

export function useRowMenu<TData extends RowData>(
  object: RecordObjectDef<TData>,
  rowActions: TableAction<TData>[] | undefined,
  rows: Row<RecordGridFeatures, TData>[],
): RowMenuController<TData> {
  const [target, setTarget] = useState<RecordRowMenuTarget | null>(null);
  const close = useCallback(() => setTarget(null), []);
  const offersAnything =
    Boolean(object.getRowHref) || (rowActions ?? []).length > 0;
  const openAt = useCallback(
    (row: TData, anchor: RowMenuAnchor) => {
      const offersThisRow =
        object.getRowHref?.(row) !== undefined ||
        visibleRowActions(rowActions, row).length > 0;
      if (!offersThisRow) return false;
      setTarget({ rowId: object.getRowId(row), ...anchor });
      return true;
    },
    [object, rowActions],
  );
  const live = target
    ? rows.find((candidate) => candidate.id === target.rowId)?.original
    : undefined;
  if (target !== null && live === undefined) setTarget(null);
  return {
    target: live === undefined ? null : target,
    row: live,
    href: live ? object.getRowHref?.(live) : undefined,
    onRowMenu: offersAnything ? openAt : undefined,
    close,
  };
}

function focusedOpener(fallback: HTMLElement): HTMLElement {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body
    ? active
    : fallback;
}

export function menuAnchorFor(
  event: MouseEvent<HTMLElement>,
  measured: HTMLElement = event.currentTarget,
): RowMenuAnchor {
  const opener = focusedOpener(measured);
  if (event.clientX > 0 || event.clientY > 0) {
    return { x: event.clientX, y: event.clientY, opener };
  }
  const row = measured.getBoundingClientRect();
  return { x: row.left + ROW_MENU_KEYBOARD_INSET, y: row.bottom, opener };
}

export function rowMenuHandler<TData extends RowData>(
  row: TData,
  onRowMenu: RowMenuController<TData>["onRowMenu"],
) {
  if (!onRowMenu) return undefined;
  return (event: MouseEvent<HTMLElement>) => {
    if (ownsContextMenu(event.target, event.currentTarget)) return;
    if (onRowMenu(row, menuAnchorFor(event))) {
      event.preventDefault();
    }
  };
}
